import React, { useCallback, useState, useRef } from 'react'
import Stack from '@mui/material/Stack'
import Calendar from './Calendar'

import { Alert } from '@mui/material'
import TimelineFooter from './TimelineFooter'
import { useTheme } from '@emotion/react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import useFrontEndConfig from '../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import PlannerEventDialog from './PlannerEventDialog'
import PlanIndicatorsRibbon from './PlanIndicatorsRibbon'
export default function Timeline (props) {
  const {
    selectedFile, setSelectedFile, isPlannerWorking, setIsPlannerWorking,
    forceReloadCalendar, publishedPlans, loadPublishedPlans, AlertElement, showAlert, dismissAlert, reloadCalendar, eventInfoOnClick, onEventClick, plannerProgress,
    setOrderEdited, orderEdited, plannerFilters, metricsVersion, bumpPlanMetricsVersion,
    eligibleDeviceIds, onMachineClick,
    // Story 175 (J3): the selected fabrication order and its "gone" signal for the Gantt borders (RN45/RN46),
    // and the "plan deleted" signal for the machine drawer (RN8 bis) -- all owned by `Planner.jsx`.
    selectedFabricationOrderId, onSelectedFabricationOrderGone, onPlanDeleted
  } = props

  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const [config] = useFrontEndConfig()
  const [eventChanges, setEventChanges] = useState(null)
  const [resourceFamily, setResourceFamily] = useState(null)
  const calendarRef = useRef(null)
  const [isFamilyViewActive, setIsFamilyViewActive] = useState(false)

  const isSameEvent = useCallback((event, familyResource) => {
    const partId = parseInt(event.extendedProps.partId, 10)
    if (selectedFile.unitCriteria.name === 'Job') {
      return partId === familyResource.partId && familyResource.jobReference === event.extendedProps.jobReferenceId
    }
    return event.extendedProps.fabricationOrderId === familyResource.fabricationOrderId && familyResource.jobReference === event.extendedProps.jobReferenceId
  }, [selectedFile?.unitCriteria?.name])

  const isThisEventFamily = useCallback((event, resourceFamily) => {
    for (const node of resourceFamily) {
      const nodeCurrentLevel = node.currentLevel !== null ? parseInt(node.currentLevel, 10) : null
      const nodeParentLevel = node.parentLevel !== null ? parseInt(node.parentLevel, 10) : null

      if (isSameEvent(event, node)) {
        return { isFamily: true, currentLevel: nodeCurrentLevel, parentLevel: nodeParentLevel }
      }
    }

    return { isFamily: false, currentLevel: null, parentLevel: null }
  }, [isSameEvent])

  // Story 153, CU9/S100 (row "suelta un job en el Gantt..."): `Calendar.updateJob` already calls this
  // very prop, with a new plan snapshot, right after its own PUT to
  // `planificationJobModification/update/{id}` succeeds -- it is the only call site of
  // `setSelectedFile` inside `Calendar.jsx`. Wrapping it here, instead of touching `Calendar.jsx`
  // (RN59/I20), turns that existing "the drag succeeded" signal into the ribbon/load-capacity
  // recalculation trigger, without ever forcing `forceReloadCalendar` (which would refetch the Gantt
  // itself and undo the point of dragging before publishing).
  const handleCalendarPlanChange = useCallback((update) => {
    bumpPlanMetricsVersion()
    setSelectedFile(update)
  }, [setSelectedFile, bumpPlanMetricsVersion])

  return (
    <>
      <PlannerEventDialog
        eventInfoOnClick={eventInfoOnClick}
        showAlert={showAlert}
        currentPlanification={selectedFile}
        onEventChange={setEventChanges}
        reloadCalendar={reloadCalendar}
        setResourceFamily={setResourceFamily}
        calendarRef={calendarRef}
        setIsFamilyViewActive={setIsFamilyViewActive}
        isThisEventFamily={isThisEventFamily}
      />
      <Stack sx={{ backgroundColor: theme.palette.background.default }} padding={2}>
        {AlertElement}
        {isPlannerWorking ? <Alert severity='info' sx={{ marginBottom: '1rem' }}>{t_('Planner is working... Please wait')}</Alert> : null}
        <PlanIndicatorsRibbon
          planificationId={selectedFile?.id}
          plannerFilters={plannerFilters}
          metricsVersion={metricsVersion}
        />
        <Calendar
          onEventClick={onEventClick}
          selectedFile={selectedFile}
          setSelectedFile={handleCalendarPlanChange}
          forceReloadCalendar={forceReloadCalendar}
          isPlannerWorking={isPlannerWorking}
          planificationId={selectedFile?.id}
          unitCriteriaName={selectedFile?.unitCriteria?.name}
          eventChanges={eventChanges}
          resourceFamily={resourceFamily}
          calendarRef={calendarRef}
          isFamilyViewActive={isFamilyViewActive}
          isThisEventFamily={isThisEventFamily}
          showAlert={showAlert}
          plannerFilters={plannerFilters}
          eligibleDeviceIds={eligibleDeviceIds}
          onMachineClick={onMachineClick}
          metricsVersion={metricsVersion}
          selectedFabricationOrderId={selectedFabricationOrderId}
          onSelectedFabricationOrderGone={onSelectedFabricationOrderGone}
        />
        <TimelineFooter
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          setIsPlannerWorking={setIsPlannerWorking}
          isPlannerWorking={isPlannerWorking}
          publishedPlans={publishedPlans}
          loadPublishedPlans={loadPublishedPlans}
          showAlert={showAlert}
          dismissAlert={dismissAlert}
          config={config}
          setIsFamilyViewActive={setIsFamilyViewActive}
          isFamilyViewActive={isFamilyViewActive}
          calendarRef={calendarRef}
          plannerProgress={plannerProgress}
          setOrderEdited={setOrderEdited}
          orderEdited={orderEdited}
          bumpPlanMetricsVersion={bumpPlanMetricsVersion}
          onPlanDeleted={onPlanDeleted}
        />
      </Stack>
    </>
  )
}
